import React, { useEffect, useRef } from 'react';
import { Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ClientBookingItem } from '../../../types/client';
import { BookingCard } from './BookingCard';

function groupByDate(bookings: ClientBookingItem[]): { date: string; items: ClientBookingItem[] }[] {
  const map = new Map<string, ClientBookingItem[]>();
  for (const b of bookings) {
    const group = map.get(b.date) ?? [];
    group.push(b);
    map.set(b.date, group);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
}

interface Props {
  bookings: ClientBookingItem[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onRebook: (booking: ClientBookingItem) => void;
}

export const HistoryBookingList: React.FC<Props> = ({
  bookings,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onRebook,
}) => {
  const { t } = useTranslation('customer');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const formatDateHeader = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    const month = t(`bookings.dateHeader.months.${d.getMonth()}`);
    const weekday = t(`bookings.dateHeader.weekdays.${d.getDay()}`);
    return `${weekday} · ${d.getDate()} ${month}`;
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div>
      {groupByDate(bookings).map(({ date, items }) => (
        <div key={date}>
          <div style={{
            position: 'sticky', top: 0, zIndex: 10,
            padding: '6px 16px',
            background: 'var(--bl-background, var(--tg-theme-bg-color, #fff))',
            borderBottom: '1px solid var(--bl-divider)',
          }}>
            <Typography.Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--bl-text-secondary)' }}>
              {formatDateHeader(date)}
            </Typography.Text>
          </div>
          {items.map((b) => (
            <BookingCard key={b.id} booking={b} onRebook={onRebook} />
          ))}
        </div>
      ))}

      {isLoadingMore && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Spin size="small" />
        </div>
      )}

      {!hasMore && bookings.length > 0 && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('bookings.loadMore.allShown')}
          </Typography.Text>
        </div>
      )}

      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
};
